import styles from './ManualSolicitationPage.module.css';
import { cn } from '@/lib/utils';
import { FC, useCallback, useEffect, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import ManualSolicitationDetails from '@/components/ManualSolicitation/ManualSolicitationDetails/ManualSolicitationDetails';
import {
	DocumentType,
	CreatePDFPayload,
	ManualSolicitation,
	ManualSolicitationPayload,
} from '@/lib/types/apiTypes';
import ManualSolicitationHeader from '@/components/ManualSolicitation/ManualSolicitationHeader/ManualSolicitationHeader';
import ManualSolicitationBody from '@/components/ManualSolicitation/ManualSolicitationBody/ManualSolicitationBody';
import ManualSolicitationDrawer from '@/components/ManualSolicitation/ManualSolicitationDrawer/ManualSolicitationDrawer';
import { useAppDispatch } from '@/lib/hooks/hooks';
import {
	ErrorHandle,
	handleResponse,
	showToast,
} from '@/lib/functions/funcUtils';
import { useNavigate } from 'react-router';
import {
	exportSolicitation,
	updateDocument,
	uploadDocumentInOrganization,
} from '@/lib/functions/apiCalls';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store/store';
import {
	clearManualSolicitation,
	endManualExport,
	setManualSolicitation,
} from '@/redux/slices/manualSolicitationSlice';
import LoaderTransparent from '@/components/Loader/LoaderTransparent/LoaderTransparent';

interface ManualSolicitationPageProps {
	exiting?: boolean;
}

const ManualSolicitationPage: FC<ManualSolicitationPageProps> = ({
	exiting,
}) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const manualSolicitationState: ManualSolicitation | null = useSelector(
		(root: RootState) => root.manualSolicitation.manualSolicitation
	);
	const [manualSolicitationData, setManualSolicitationData] =
		useState<ManualSolicitation | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const formatPayload = (file: any, additionalFiles: File[] | null) => {
		if (!manualSolicitationData) return;
		return {
			file: file,
			name: manualSolicitationData.name,
			type: manualSolicitationData.type,
			additionalResources: additionalFiles,
			isSolicited: manualSolicitationData.isSolicited,
			hash: manualSolicitationData.hash,
		};
	};

	const onSolicitationSubmit = async (
		formData: CreatePDFPayload,
		additionalFiles: File[] | null
	) => {
		try {
			setIsLoading(true);
			const response = await exportSolicitation(formData);
			handleResponse(response);
			const blob = new Blob([response as ArrayBuffer]);
			const file = new File([blob], 'solicitation.pdf', {
				type: 'application/pdf',
			});
			const payload = formatPayload(file, additionalFiles);
			if (!payload) {
				showToast('error', 'Something went wrong');
				return;
			}
			const ingestResponse = await uploadDocumentInOrganization(payload);
			handleResponse(ingestResponse);
			dispatch(clearManualSolicitation());
			setIsLoading(false);
			navigate('/solicitations');
		} catch (error: unknown) {
			ErrorHandle(dispatch, error);
			setIsLoading(false);
		}
	};

	const exportDocument = async (
		formData: CreatePDFPayload,
		type: DocumentType
	) => {
		try {
			setIsLoading(true);
			const response = await exportSolicitation(formData, type);
			handleResponse(response);
			const blob = new Blob([response as ArrayBuffer]);
			const link = document.createElement('a');
			link.href = window.URL.createObjectURL(blob);
			link.download = `solicitation.${type}`;
			link.click();
			dispatch(endManualExport());
			setIsLoading(false);
		} catch (error: unknown) {
			ErrorHandle(dispatch, error);
			setIsLoading(false);
			dispatch(endManualExport());
		}
	};

	const handleManualSolicitation = (
		solicitation: ManualSolicitationPayload
	) => {
		updateManualSolicitation(solicitation);
	};

	const updateManualSolicitation = async (
		solicitation: ManualSolicitationPayload
	) => {
		try {
			if (manualSolicitationData?.id) {
				setIsLoading(true);
				const response = await updateDocument(
					solicitation,
					manualSolicitationData.id
				);
				handleResponse(
					response,
					'Project details has been updated successfully.'
				);
				const updatedSolicitationState = {
					...manualSolicitationData,
					...solicitation,
				};
				dispatch(setManualSolicitation(updatedSolicitationState));
				setManualSolicitationData(updatedSolicitationState);
				setIsLoading(false);
			}
		} catch (error: unknown) {
			ErrorHandle(dispatch, error);
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (manualSolicitationState) {
			setManualSolicitationData(manualSolicitationState);
		} else {
			navigate('/');
		}
	}, []);

	//Animation Handling

	const control = useAnimation();

	const startOpeningAnimation = useCallback(() => {
		control.set({ opacity: 0, y: '400px' });
		control.start({
			opacity: 1,
			y: 0,
			transition: { duration: 0.8, delay: 0.3 },
		});
	}, [control]);

	const startExitingAnimation = useCallback(
		(exiting) => {
			if (exiting === true) {
				control.start({ opacity: 0, transition: { duration: 0.25 } });
			}
		},
		[control]
	);

	useEffect(() => {
		if (exiting) {
			startExitingAnimation(exiting);
		}
	}, [exiting, startExitingAnimation]);

	useEffect(() => {
		startOpeningAnimation();
	}, [startOpeningAnimation]);

	return (
		<div className={cn(styles.wrapper)}>
			<motion.div
				className="grid w-full grid-cols-11 overflow-y-auto hidden-scroll"
				animate={control}
			>
				<div className="flex flex-col col-span-8 px-2 pt-6">
					<ManualSolicitationHeader />
					<ManualSolicitationBody>
						<ManualSolicitationDetails
							submitForm={onSolicitationSubmit}
							exportDocument={exportDocument}
						/>
					</ManualSolicitationBody>
				</div>
				<div className="col-span-3 overflow-y-hidden">
					<ManualSolicitationDrawer
						manualSolicitation={manualSolicitationData}
						onChange={handleManualSolicitation}
					/>
				</div>
				{isLoading && <LoaderTransparent />}
			</motion.div>
		</div>
	);
};

export default ManualSolicitationPage;
